import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useWebRouter } from "~utils/next-router/use-web-router";
import {
	GET_MAP_ACCESS_METHOD_TYPE,
	GetMapByAuthorizedUserQuery,
	GetMapByHiddenIdQuery,
	GetMapByPasswordQuery,
	GetMapByPublicQuery,
	Map,
	MapTool,
} from "~map-view/types/__generated/gql/graphql";
import { sortMapNestedObject } from "~utils/functions/sortAscendingByCreatedAt";
import { useGetCurrentMapAccessMethod } from "../useGetMapAccessMethod";
import { getMapByAuthorizedUser } from "./getMapByAuthorizedUser";
import { getMapByHiddenId } from "./getMapByHiddenId";
import { getMapByPassword } from "./getMapByPassword";
import { getMapByPublic } from "./getMapByPublic";

export const getCurrentMapQueryKey = (id: string) => ["getCurrentMap", id];

export const useGetCurrentMap = (): UseQueryResult<
	| GetMapByAuthorizedUserQuery["getMapByAuthorizedUser"]
	| GetMapByHiddenIdQuery["getMapByHiddenId"]
	| GetMapByPasswordQuery["getMapByPassword"]
	| GetMapByPublicQuery["getMapByPublic"]
> => {
	const { _query } = useWebRouter();

	const { data } = useGetCurrentMapAccessMethod();

	return useQuery({
		enabled: !!_query.mapId && !!data,
		queryKey: getCurrentMapQueryKey(_query.mapId!),
		// @ts-ignore
		queryFn: () => {
			switch (data!.type) {
				case GET_MAP_ACCESS_METHOD_TYPE.AUTHORIZED_USER:
					return getMapByAuthorizedUser({
						id: _query.mapId!,
					}).then(sortMapNestedObject);
				case GET_MAP_ACCESS_METHOD_TYPE.HIDDEN_ID:
					return getMapByHiddenId({
						id: _query.mapId!,
					}).then(sortMapNestedObject);
				case GET_MAP_ACCESS_METHOD_TYPE.PASSWORD:
					return getMapByPassword({
						id: _query.mapId!,
						password:
							sessionStorage.getItem(`map-password:${_query.mapId}`) ?? "",
					}).then(sortMapNestedObject);
				case GET_MAP_ACCESS_METHOD_TYPE.PUBLIC:
					return getMapByPublic({
						id: _query.mapId!,
					}).then(sortMapNestedObject);
			}
		},
	});
};

export const useGetCurrentMapCache = () => {
	const { _query } = useWebRouter();

	return useQuery({
		queryKey: getCurrentMapQueryKey(_query.mapId!),
	});
};

export const isToolEnabled = ({
	map,
	tool,
}: {
	map: Pick<Map, "tool">;
	tool: keyof MapTool;
}) => {
	return map.tool?.[tool] ?? false;
};
